import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import * as store from '@daas-consulting/core-frontend-store';
import { compareRemitoItems } from '@daas-consulting/production/remitos/cross/ordering';
import {
  RemitosService,
  RemitosServiceId,
} from '@daas-consulting/production/remitos/frontend/data_contracts';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import {
  congrats,
  errorMessage,
  loader,
} from '@daas-consulting/production/shared/frontend/store';
import { SetFormDisabled, SetFormEnabled } from '@ngxs/form-plugin';
import { Navigate } from '@ngxs/router-plugin';
import {
  Action,
  State,
  StateContext,
  Store,
  ofActionDispatched,
} from '@ngxs/store';
import { EMPTY, MonoTypeOperatorFunction, catchError, take, tap } from 'rxjs';
import { v4 as uuid } from 'uuid';
import * as actions from './remitos-form.actions';
import * as consts from './remitos-form.const';
import { RemitosFormStateModel } from './remitos-form.state-model';

// HttpStatusCode de @angular/common/http es const enum: no existe en runtime bajo jest.
const BAD_REQUEST_STATUS = 400;

export const defaults: RemitosFormStateModel = {
  dataForm: store.getFormModelDefault<models.RemitoFormModel>(),
  requestItem: null,
  activeItem: null,
  availablePrescriptions: [],
  institutions: [],
  activePurchaseOrders: [],
  lines: [],
  articles: [],
  additionalArticles: [],
  selectedInstitution: null,
  selectedPurchaseOrder: null,
  selectedLineId: null,
  selectedPatientName: null,
};

@State<RemitosFormStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class RemitosFormState {
  readonly #store: Store;
  readonly #service: RemitosService;
  readonly #eventHelper: store.EventHelper;

  constructor(
    store: Store,
    @Inject(RemitosServiceId) service: RemitosService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper
  ) {
    this.#store = store;
    this.#service = service;
    this.#eventHelper = eventHelper;
    this._connectEvents();
  }

  @Action(actions.GetDataRequest)
  getDataRequest(): void {
    this.#service
      .getFormData$()
      .pipe(
        take(1),
        tap((item) => {
          this.#store.dispatch(new actions.GetDataSuccessful(item));
        }),
        catchError((error) => {
          this.#store.dispatch(new actions.GetDataFailure(error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetDataSuccessful)
  getDataSuccessful(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.GetDataSuccessful
  ): void {
    ctx.patchState({
      institutions: payload.institutions.sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
      lines: payload.lines.sort((a, b) =>
        a.code.trim().localeCompare(b.code.trim())
      ),
      // El selector los muestra como "CÓDIGO — Nombre", así que el orden
      // visible tiene que ser por código.
      articles: [...payload.articles].sort((a, b) =>
        a.code.trim().localeCompare(b.code.trim(), undefined, { numeric: true })
      ),
    });
  }

  @Action(actions.GetDataFailure)
  getDataFailure(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.GetDataFailure
  ) {
    const action = new errorMessage.actions.ShowError(payload);
    return ctx.dispatch(action);
  }

  @Action(actions.SetInstitution)
  setInstitution(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.SetInstitution
  ): void {
    const state = ctx.getState();
    const institution = state.institutions.find(
      (i) => i.institutionId === payload
    );

    ctx.patchState({
      selectedInstitution: institution || null,
      selectedPurchaseOrder: null,
      selectedLineId: null,
      selectedPatientName: null,
      availablePrescriptions: [],
      activePurchaseOrders: [],
      additionalArticles: [],
    });

    if (institution) {
      this.#store.dispatch(new actions.GetAvailablePrescriptions(payload));
      this.#store.dispatch(new actions.GetActivePurchaseOrders(payload));
    }
  }

  @Action(actions.GetAvailablePrescriptions)
  getAvailablePrescriptions(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.GetAvailablePrescriptions
  ): void {
    const cancelCondition = (
      x: typeof actions.GetAvailablePrescriptions
    ): boolean => {
      return (
        (x as unknown as actions.GetAvailablePrescriptions).payload !== payload
      );
    };

    this.#service
      .getAvailablePrescriptions$(payload)
      .pipe(
        take(1),
        this.#eventHelper.makeCancelOperator(
          actions.GetAvailablePrescriptions,
          cancelCondition,
          () => {
            const action = new loader.actions.HideLoading();
            ctx.dispatch(action);
          }
        ) as MonoTypeOperatorFunction<models.AvailablePrescriptionModel[]>,
        tap((prescriptions) => {
          this.#store.dispatch(
            new actions.GetAvailablePrescriptionsSuccessful(prescriptions)
          );
        }),
        catchError((error) => {
          this.#store.dispatch(
            new actions.GetAvailablePrescriptionsFailure(error)
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetAvailablePrescriptionsSuccessful)
  getAvailablePrescriptionsSuccessful(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.GetAvailablePrescriptionsSuccessful
  ): void {
    ctx.patchState({ availablePrescriptions: payload });
  }

  @Action(actions.GetActivePurchaseOrders)
  getActivePurchaseOrders(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.GetActivePurchaseOrders
  ): void {
    const cancelCondition = (
      x: typeof actions.GetActivePurchaseOrders
    ): boolean => {
      return (
        (x as unknown as actions.GetActivePurchaseOrders).payload !== payload
      );
    };

    this.#service
      .getActivePurchaseOrders$(payload)
      .pipe(
        take(1),
        this.#eventHelper.makeCancelOperator(
          actions.GetActivePurchaseOrders,
          cancelCondition,
          () => {
            const action = new loader.actions.HideLoading();
            ctx.dispatch(action);
          }
        ) as MonoTypeOperatorFunction<models.PurchaseOrderFormData[]>,
        tap((orders) => {
          this.#store.dispatch(
            new actions.GetActivePurchaseOrdersSuccessful(orders)
          );
        }),
        catchError((error) => {
          this.#store.dispatch(
            new actions.GetActivePurchaseOrdersFailure(error)
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.GetActivePurchaseOrdersSuccessful)
  getActivePurchaseOrdersSuccessful(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.GetActivePurchaseOrdersSuccessful
  ): void {
    ctx.patchState({ activePurchaseOrders: payload });
    // Auto-select the first active purchase order if only one exists
    if (payload.length === 1) {
      ctx.patchState({ selectedPurchaseOrder: payload[0] });
    }
  }

  @Action(actions.SetPurchaseOrder)
  setPurchaseOrder(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.SetPurchaseOrder
  ): void {
    const state = ctx.getState();
    const purchaseOrder = state.activePurchaseOrders.find(
      (o) => o.purchaseOrderId === payload
    );
    ctx.patchState({ selectedPurchaseOrder: purchaseOrder || null });
  }

  @Action(actions.SetLine)
  setLine(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.SetLine
  ): void {
    const state = ctx.getState();
    const line = state.lines.find((l) => l.lineId === payload);
    if (!line) {
      return;
    }

    // Deselect all prescriptions when changing line
    const availablePrescriptions = state.availablePrescriptions.map((p) => ({
      ...p,
      selected: false,
    }));

    ctx.patchState({
      selectedLineId: payload,
      availablePrescriptions,
    });
  }

  @Action(actions.SetPatient)
  setPatient(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.SetPatient
  ): void {
    const state = ctx.getState();
    const availablePrescriptions = state.availablePrescriptions.map((p) => ({
      ...p,
      selected: false,
    }));

    ctx.patchState({
      selectedPatientName: payload,
      selectedLineId: null,
      availablePrescriptions,
    });
  }

  @Action(actions.TogglePrescriptionSelection)
  togglePrescriptionSelection(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.TogglePrescriptionSelection
  ): void {
    const state = ctx.getState();
    const availablePrescriptions = state.availablePrescriptions.map((p) =>
      p.prescriptionId === payload.prescriptionId
        ? { ...p, selected: payload.selected }
        : p
    );
    ctx.patchState({ availablePrescriptions });
  }

  @Action(actions.ToggleAllPrescriptionsSelection)
  toggleAllPrescriptionsSelection(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.ToggleAllPrescriptionsSelection
  ): void {
    const state = ctx.getState();
    let targetPrescriptions = state.availablePrescriptions;
    if (
      state.selectedInstitution?.remitoGroupingRule === 'PER_PATIENT' &&
      state.selectedPatientName
    ) {
      targetPrescriptions = targetPrescriptions.filter(
        (p) => p.patientName === state.selectedPatientName
      );
    }
    const targetIds = new Set(targetPrescriptions.map((p) => p.prescriptionId));
    const availablePrescriptions = state.availablePrescriptions.map((p) =>
      targetIds.has(p.prescriptionId) ? { ...p, selected: payload.selected } : p
    );
    ctx.patchState({ availablePrescriptions });
  }

  @Action(actions.AssignLineToPrescription)
  assignLineToPrescription(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.AssignLineToPrescription
  ): void {
    const state = ctx.getState();
    const availablePrescriptions = state.availablePrescriptions.map((p) =>
      p.prescriptionId === payload.prescriptionId
        ? { ...p, assignedLineId: payload.lineId }
        : p
    );
    ctx.patchState({ availablePrescriptions });
  }

  @Action(actions.AddAdditionalArticle)
  addAdditionalArticle(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.AddAdditionalArticle
  ): void {
    const state = ctx.getState();
    const article = state.articles.find(
      (a) => a.articleId === payload.articleId
    );
    if (!article) {
      return;
    }

    const normalizedLot = payload.lot ?? null;
    const normalizedExpirationDate = payload.expirationDate ?? null;
    const existing = state.additionalArticles.find(
      (a) =>
        a.articleId === payload.articleId &&
        a.lot === normalizedLot &&
        a.expirationDate === normalizedExpirationDate
    );
    if (existing) {
      const additionalArticles = state.additionalArticles.map((a) =>
        a.articleId === payload.articleId &&
        a.lot === normalizedLot &&
        a.expirationDate === normalizedExpirationDate
          ? { ...a, quantity: a.quantity + payload.quantity }
          : a
      );
      ctx.patchState({ additionalArticles });
    } else {
      ctx.patchState({
        additionalArticles: [
          ...state.additionalArticles,
          {
            articleId: article.articleId,
            code: article.code,
            name: article.name,
            articleType: article.type,
            quantity: payload.quantity,
            lot: payload.lot ?? null,
            expirationDate: payload.expirationDate ?? null,
          },
        ],
      });
    }
  }

  @Action(actions.RemoveAdditionalArticle)
  removeAdditionalArticle(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.RemoveAdditionalArticle
  ): void {
    const state = ctx.getState();
    const additionalArticles = state.additionalArticles.filter(
      (a) =>
        a.articleId !== payload.articleId ||
        a.lot !== payload.lot ||
        a.expirationDate !== payload.expirationDate
    );
    ctx.patchState({ additionalArticles });
  }

  @Action(actions.Save)
  save(ctx: StateContext<RemitosFormStateModel>): void {
    const state = ctx.getState();
    const form = state.dataForm;
    if (form.status === store.FormStatusTypes.VALID) {
      this.#store.dispatch(new actions.SaveRequest());
    } else {
      const err = new ErrorFormInvalid('remitos', form.errors);
      this.#store.dispatch(new actions.SaveFailure(err));
    }
  }

  @Action(actions.SaveRequest)
  saveRequest(ctx: StateContext<RemitosFormStateModel>): void {
    const state = ctx.getState();
    const remito = this._getRemitoFromModel(state);

    this.#service
      .save$(remito)
      .pipe(
        take(1),
        tap(() => {
          this.#store.dispatch(new actions.SaveSuccessful());
        }),
        catchError((error) => {
          this.#store.dispatch(new actions.SaveFailure(error));
          return EMPTY;
        })
      )
      .subscribe();
  }

  @Action(actions.SaveSuccessful)
  saveSuccessful(): void {
    const action = new Navigate(['/remitos/list']);
    this.#store.dispatch(action);
  }

  @Action(actions.SaveFailure)
  saveFailure(
    ctx: StateContext<RemitosFormStateModel>,
    { payload }: actions.SaveFailure
  ) {
    const action = new errorMessage.actions.ShowError(
      this._getSaveError(payload)
    );
    return ctx.dispatch(action);
  }

  private _getSaveError(payload: Error): Error {
    if (
      payload instanceof HttpErrorResponse &&
      payload.status === BAD_REQUEST_STATUS &&
      this._isArticleIdValidationError(payload)
    ) {
      return new Error(
        'No se puede emitir el remito: uno o más ítems no tienen un artículo asociado. ' +
          'Las bolsas deben estar cargadas como artículos para poder emitir el remito.'
      );
    }
    return payload;
  }

  private _isArticleIdValidationError(payload: HttpErrorResponse): boolean {
    const messages = payload.error?.message;
    return (
      Array.isArray(messages) &&
      messages.some((m) => typeof m === 'string' && m.includes('articleId'))
    );
  }

  private _connectEvents(): void {
    this.#eventHelper.connectEvents(
      actions.GetData,
      loader.actions.ShowLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.GetData,
      new SetFormDisabled(consts.dataFormPath),
      ofActionDispatched,
      false
    );

    this.#eventHelper.connectEvents(actions.GetData, actions.GetDataRequest);

    this.#eventHelper.connectEvents(
      actions.GetDataSuccessful,
      loader.actions.HideLoading
    );

    this.#eventHelper.connectEvents(
      actions.GetDataSuccessful,
      new SetFormEnabled(consts.dataFormPath),
      undefined,
      false
    );

    this.#eventHelper.connectEvents(
      actions.GetDataFailure,
      loader.actions.HideLoading
    );

    this.#eventHelper.connectEvents(
      actions.Save,
      loader.actions.ShowLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.SaveRequest,
      new SetFormDisabled(consts.dataFormPath),
      ofActionDispatched,
      false
    );

    this.#eventHelper.connectEvents(
      actions.SaveFailure,
      loader.actions.HideLoading
    );

    this.#eventHelper.connectEvents(
      actions.SaveFailure,
      new SetFormEnabled(consts.dataFormPath),
      undefined,
      false
    );

    this.#eventHelper.connectEvents(
      actions.SaveSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.SaveSuccessful,
      new SetFormEnabled(consts.dataFormPath),
      undefined,
      false
    );

    this.#eventHelper.connectEvents(
      actions.SaveSuccessful,
      congrats.actions.ShowCongrats,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.GetAvailablePrescriptions,
      loader.actions.ShowLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.GetAvailablePrescriptionsSuccessful,
      loader.actions.HideLoading
    );

    this.#eventHelper.connectEvents(
      actions.GetAvailablePrescriptionsFailure,
      loader.actions.HideLoading
    );

    this.#eventHelper.connectEvents(
      actions.GetActivePurchaseOrders,
      loader.actions.ShowLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.GetActivePurchaseOrdersSuccessful,
      loader.actions.HideLoading
    );

    this.#eventHelper.connectEvents(
      actions.GetActivePurchaseOrdersFailure,
      loader.actions.HideLoading
    );
  }

  private _getRemitoFromModel(
    state: RemitosFormStateModel
  ): models.RemitoFormModel {
    const header = state.dataForm.model as models.RemitoFormModel;
    const selectedPrescriptions = state.availablePrescriptions.filter(
      (p) => p.selected
    );

    const line =
      state.lines.find((l) => l.lineId === state.selectedLineId) ?? null;
    const lineCode = line?.code || '';
    const lineDescription = line?.description || '';
    const items: models.RemitoItemFormModel[] = selectedPrescriptions.map(
      (p) => {
        const article = state.articles.find((a) => a.articleId === p.articleId);
        return {
          articleId: p.articleId,
          itemCode: lineCode,
          // El renglón describe al ítem del remito; el nombre del artículo bolsa
          // es su volumen y sirve de respaldo si el renglón no tiene descripción
          // (la columna persistida es NOT NULL).
          itemDescription: lineDescription || p.articleName,
          articleType: article?.type ?? 'bag',
          quantity: 1,
          lot: p.assercaId,
          expirationDate: p.expirationDate,
        };
      }
    );

    // Add additional articles
    const additionalItems: models.RemitoItemFormModel[] =
      state.additionalArticles.map((a) => ({
        articleId: a.articleId,
        itemCode: a.code,
        itemDescription: a.name,
        articleType: a.articleType,
        quantity: a.quantity,
        lot: a.lot,
        expirationDate: a.expirationDate,
      }));

    const institution = state.selectedInstitution;
    const purchaseOrder = state.selectedPurchaseOrder;

    return {
      remitoId: header?.remitoId || uuid(),
      institutionId: institution?.institutionId || header?.institutionId || '',
      institutionName: institution?.name || header?.institutionName || '',
      financierId: institution?.financierId || header?.financierId || '',
      financierName: institution?.financierName || header?.financierName || '',
      financierIvaCondition:
        institution?.financierIvaCondition ??
        header?.financierIvaCondition ??
        null,
      purchaseOrderId:
        purchaseOrder?.purchaseOrderId || header?.purchaseOrderId || '',
      purchaseOrderNumber:
        purchaseOrder?.number || header?.purchaseOrderNumber || '',
      lineId: state.selectedLineId || '',
      lineCode,
      lineDescription,
      observations: header?.observations ?? null,
      items: [...items, ...additionalItems].sort(compareRemitoItems),
    };
  }
}

export class ErrorFormInvalid extends Error {
  constructor(readonly feature: string, readonly errors: unknown) {
    super('formulario invalido. por favor verifique los datos cargados.');
  }
}
